import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PageWrapper from '@components/PageWrapper';
import LoadingProgress from '@components/LoadingProgress';
import BottomButtonsWrapper from '@components/BottomButtonsWrapper';
import { useNotification } from '@components/Notification';
import { useMytaverse } from '@providers/MytaverseProvider';
import { CurrentScreenEnum } from '@providers/MytaverseProvider/interfaces';
import ParticipantsService from '@services/ParticipantsService';
import { EventStatus, IEvent } from '@interfaces/event';
import { AnalyticsType } from '@interfaces/analytics';
import {
  getExternalUrlHash,
  compareTokenHash,
  createHashFromToken,
} from '@helpers/externalUrlHash';
import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import { getClientSpecificSDCarouselEventsTransform } from '@helpers/clientSpecificUtils';
import ROUTES from '@constants/routes';
import SelectEventPageView from './SelectEventPageView';
import {
  useClientSpecificCarouselEventsTransform,
  useClientSpecificNoEventsNotification,
} from '@hooks/clientSpecificUtils';
import { groupEvents } from '@helpers/events';
import { filterVisibleEvents, isExternalEvent } from './helper';
import { removeLocalStorageValue } from '@helpers/storage';
import { StorageTypes, StorageValues } from '@interfaces/storage';
import Assistant from '@core/assistant';

const SelectEventPage = () => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<IEvent>();

  const { user, token, trackAnalytics, setCurrentScreen } = useMytaverse();
  const { showNotification, getBadRequestNotification } = useNotification();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const showNoEventsNotification = useClientSpecificNoEventsNotification();

  const carouselEventsTransform = useClientSpecificCarouselEventsTransform();

  useEffect(() => {
    setCurrentScreen(CurrentScreenEnum.DEFAULT);

    removeLocalStorageValue(
      StorageValues.TransitionLoadingScreen,
      StorageTypes.Session,
    );
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);

    const participantEvents =
      await ParticipantsService.getCurrentParticipantEvents();

    if (
      participantEvents.events.length === 0 &&
      user &&
      showNoEventsNotification
    ) {
      const message = `The email address ${
        user.email || ''
      } is not invited to any worlds. Are you sure this is the correct email address?`;
      showNotification(getBadRequestNotification({ message }));

      console.error(message);

      setLoading(false);

      return;
    }

    if (participantEvents.events.length !== 0) {
      const events = await Promise.all(
        participantEvents.events
          .map((participantEvent) => ({
            ...(participantEvent.event as IEvent),
            isDisabled: participantEvent.isDisabled,
            state: participantEvent.state,
            inviteStartTime:
              participantEvent.startDate ||
              participantEvent.event?.startTime ||
              0,
          }))
          .filter(({ endTime, status }) => {
            const oneMinutesFromNow = Date.now() + 60 * 1000;

            return (
              [EventStatus.ACTIVE, EventStatus.SCHEDULED].includes(status) &&
              (endTime === 0 || endTime * 1000 > oneMinutesFromNow)
            );
          }),
      );

      groupEvents(events);

      getClientSpecificSDCarouselEventsTransform(events, user);

      setEvents(filterVisibleEvents(events));
    }

    setLoading(false);
  }, [setLoading, setEvents, pathname, user]);

  const goToNextScreen = useCallback(async () => {
    sendCustomSentryBreadcrumb({
      message: `select event "${selectedEvent?.name}"`,
    });

    trackAnalytics(AnalyticsType.EventSelect, {
      eventId: selectedEvent?.eventId,
    });

    if (
      selectedEvent &&
      isExternalEvent(selectedEvent) &&
      selectedEvent.externalUrl
    ) {
      const hashedToken = await createHashFromToken(token);
      window.location.href = [
        selectedEvent.externalUrl,
        selectedEvent.externalUrl.includes('?') ? '&' : '?',
        `authHash=${hashedToken}`,
      ].join('');
    } else {
      navigate(ROUTES.SELECT_AVATAR_PROVIDER(selectedEvent?.eventId));
    }
  }, [trackAnalytics, selectedEvent]);

  useEffect(() => {
    if (!events.length) {
      loadEvents();
    }

    const externalEventTokenHash = getExternalUrlHash();

    if (externalEventTokenHash) {
      compareTokenHash(token, externalEventTokenHash).then(
        (isTokenHashValid) => {
          if (!isTokenHashValid) {
            const message = 'External event auth token hash is invalid!';
            showNotification(getBadRequestNotification({ message }));
          }
        },
      );
    }
  }, [events, loadEvents]);

  const carouselEvents = useMemo(
    () => carouselEventsTransform(events),
    [events],
  );

  if (!carouselEvents.length && loading) {
    return <LoadingProgress height="30vh" />;
  }

  const BottomElement = (
    <BottomButtonsWrapper
      hideLeftButton
      rightButtonDisabled={!selectedEvent}
      rightButtonText={translate('next')}
      rightButtonClick={goToNextScreen}
    />
  );

  return (
    <PageWrapper
      bottomElement={BottomElement}
      title={
        !events.length
          ? translate('eventPage.titleNoEvents')
          : translate('eventPage.title')
      }
    >
      <Assistant isSelectEventPage={true} />
      <SelectEventPageView
        events={carouselEvents}
        selectedEvent={selectedEvent}
        setSelectedEvent={setSelectedEvent}
      />
    </PageWrapper>
  );
};

export default SelectEventPage;
